import { createContext, useContext, useState, ReactNode, useCallback, useMemo, forwardRef } from 'react';
import { css } from '@emotion/react';
import {
  NotificationType,
  NotificationType_index as NotificationTypeIndex,
} from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { AnimatePresence, motion, MotionProps } from 'motion/react';
import { useQuery, useQueryClient } from 'react-query';
import { NotificationApi, NotificationQueries } from '@frontend/api-notifications';
import { useAppScopeStore } from '@frontend/scope';
import { allowedNotificationTypes } from '@frontend/settings-routing';
import { WeavePopNotification, CommonHTMLAttributes } from '@frontend/types';
import { theme } from '@frontend/theme';
import { useNotificationContext } from './notification-provider';
import { MuteFlowManager } from './shared/muted-pops';
import { NewNotificationsProvider } from './shared/new-notifications-context';
import { Notification } from './shared/notification';
import { OpenNotifications } from './shared/slider';

export const TransientQueue = () => {
  return (
    <NewNotificationsProvider value={true}>
      <AnimatePresence>{<NewNotificationsExperience />}</AnimatePresence>
    </NewNotificationsProvider>
  );
};

interface MuteFlowOrchestratorProps {
  isActive: boolean;
  notificationType: WeavePopNotification['type'] | null;
  onDismiss: () => void;
  x: string;
  y: string;
}

export const MuteFlowOrchestrator = ({ isActive, notificationType, onDismiss, x, y }: MuteFlowOrchestratorProps) => {
  const { selectedLocationIds } = useAppScopeStore();
  const { mutateAsync: updateNotificationSetting } = NotificationQueries.useNotificationSettingsMutation({});
  const queryClient = useQueryClient();
  const { removeAll } = useTransientQueueContext();

  const { data: notificationSettings } = useQuery({
    queryKey: [selectedLocationIds[0], 'notification-settings', selectedLocationIds[0]],
    queryFn: async () => await NotificationApi.getAllNotificationSettings(selectedLocationIds[0]),
    // enabled: !!selectedLocationIds?.[0],
    // We will re-enable this when we figure out a better way to handle muting notifications
    enabled: false,
  });

  const filteredNotificationSettings = useMemo(
    () => notificationSettings?.filter((setting) => allowedNotificationTypes.find((type) => type.id === setting.id)),
    [notificationSettings]
  );

  const handleConfirmMuteAll = useCallback(async () => {
    removeAll();
    try {
      await Promise.all(
        allowedNotificationTypes.map((notification) =>
          updateNotificationSetting({
            id: NotificationTypeIndex[notification.firestoreType] as unknown as NotificationType,
            locationId: selectedLocationIds?.[0],
            value: false,
          })
        )
      );
    } catch (error) {
      console.error('Failed to update notification settings:', error);
    }
  }, [removeAll, updateNotificationSetting, selectedLocationIds]);

  const handleReverseMuteAll = useCallback(async () => {
    removeAll();
    try {
      if (!filteredNotificationSettings) return;

      await Promise.all(
        filteredNotificationSettings.map((notification) =>
          updateNotificationSetting({
            id: notification.notificationType,
            locationId: selectedLocationIds?.[0],
            value: true,
          })
        )
      );
    } catch (error) {
      console.error('Failed to update notification settings:', error);
    }
  }, [removeAll, updateNotificationSetting, selectedLocationIds, filteredNotificationSettings]);

  const handleMuteFlowCompleted = useCallback(() => {
    onDismiss();
    queryClient.invalidateQueries([selectedLocationIds[0], 'notification-settings', selectedLocationIds[0]]);
  }, [onDismiss, queryClient, selectedLocationIds]);

  const handleManageMuteSettings = useCallback(() => {
    handleReverseMuteAll();
    onDismiss();
  }, [handleReverseMuteAll, onDismiss]);

  if (!isActive || !notificationType) {
    return null;
  }

  return (
    <div
      style={{
        ...(x === 'top' ? { top: 73 } : { bottom: 0 }),
        ...(y === 'right' ? { right: 57 } : { left: 0 }),
        position: 'fixed',
        zIndex: theme.zIndex.alerts + 1,
        padding: theme.spacing(1),
      }}
    >
      <MuteFlowManager
        notificationType={notificationType}
        onMuteConfirmed={handleConfirmMuteAll}
        onFlowCompleted={handleMuteFlowCompleted}
        onFlowDismissed={onDismiss}
        onManageMuteSettings={handleManageMuteSettings}
      />
    </div>
  );
};

const NewNotificationsExperience = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { queue, removeAll } = useTransientQueueContext();
  const hasPreference = queue.some((item) => item.type === 'preference');
  const ctx = useNotificationContext();

  return (
    <AnimatePresence>
      <motion.div
        data-testid='new-notifications-experience-transient-queue'
        style={{
          display: queue.length ? 'flex' : 'none',
          zIndex: theme.zIndex.alerts,
          flexDirection: 'column',
          gap: theme.spacing(1),
          padding: theme.spacing(1),
          maxHeight: 'calc(100vh - 73px)',
          overflowY: 'auto',
          paddingBottom: theme.spacing(4),
          alignItems: 'flex-end',
        }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      >
        {queue.length > 1 && (
          <OpenNotifications
            isExpanded={isExpanded}
            onExpand={() => setIsExpanded((prev) => !prev)}
            disableClearAll={hasPreference}
            onClearAll={removeAll}
            total={queue.length}
            queue={queue}
          />
        )}
        {queue.slice(0, isExpanded ? undefined : 1).map((notificationInView) => (
          <Notification
            key={notificationInView.id}
            notification={notificationInView}
            onEmit={(action, notification) => {
              ctx.emit(notification.type, {
                action: typeof action === 'string' ? action : action.action,
                payload: typeof action === 'string' ? undefined : action?.payload,
                notification: notification as any,
              });
            }}
            ContainerComponent={TransientItemContainer}
            containerProps={{
              id: notificationInView.id,
              xAlignment: 'center',
              'data-trackingId': `new-notification-action-${notificationInView.type}-view`,
              'data-testid': 'new-notification-pop-instance',
            }}
            containerStyles={css`
              border-radius: ${theme.borderRadius.medium};
              box-shadow: ${theme.shadows.heavy};
              position: relative;
              width: 350px;
            `}
          >
            {!isExpanded && <Notification.ProgressBarNew />}
            <Notification.Content stacked={queue.length > 1} />
          </Notification>
        ))}
      </motion.div>
    </AnimatePresence>
  );
};

export type AddOptions = {
  duration?: number;
};

type TransientQueueContextType = {
  queue: WeavePopNotification[];
  add: (notification: WeavePopNotification, options?: AddOptions) => void;
  remove: (id: string) => void;
  removeAll: () => void;
  update: (notification: WeavePopNotification) => void;
  setActiveIndex: (index: number) => void;
  activeIndex: number;
};

const TransientQueueContext = createContext<TransientQueueContextType>({
  queue: [],
  add: (_item, _options) => {
    console.warn('Notification Queue Not Initialized');
  },
  remove: (_id) => {},
  removeAll: () => {},
  update: (_item) => {},
  setActiveIndex: (_index) => {},
  activeIndex: 0,
});

const useTransientQueueContext = () => useContext(TransientQueueContext);

export const TransientQueueProvider = ({ children }: { children: ReactNode }) => {
  const [queue, setQueue] = useState<WeavePopNotification[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const remove = useCallback((id: string) => {
    setQueue((prev) => {
      const newQueue = prev.filter((item) => item.id !== id);

      return newQueue;
    });
  }, []);

  const add = useCallback((notification: WeavePopNotification) => {
    setQueue((prev) => [...prev, notification]);
  }, []);

  const removeAll = useCallback(() => {
    setActiveIndex(0);
    setQueue([]);
  }, []);

  const update = useCallback((notification: WeavePopNotification) => {
    setQueue((prev) => prev.map((p) => (p.id === notification.id ? ({ ...p, ...notification } as typeof p) : p)));
  }, []);

  return (
    <TransientQueueContext.Provider value={{ queue, add, remove, removeAll, update, setActiveIndex, activeIndex }}>
      {children}
    </TransientQueueContext.Provider>
  );
};

export { useTransientQueueContext as useTransientQueue };

type TransientItemContainerProps = {
  children: ReactNode;
  id: string;
  onClick?: (id: string) => void;
  xAlignment: 'left' | 'right';
} & CommonHTMLAttributes &
  MotionProps;

export const TransientItemContainer = forwardRef<HTMLDivElement, TransientItemContainerProps>(
  ({ children, id, onClick, xAlignment, ...rest }, ref) => {
    return (
      <motion.div ref={ref} layout='position' onClick={() => onClick?.(id)} {...rest}>
        {children}
      </motion.div>
    );
  }
);
TransientItemContainer.displayName = 'TransientItemContainer';
